/**
 * Хук для определения размера экрана и адаптивных breakpoints
 * 
 * Breakpoints (согласно Tailwind):
 * - xs: < 640px (очень малые экраны)
 * - sm: 640px - 767px (малые экраны)
 * - md: 768px - 1023px (планшеты)
 * - lg: 1024px - 1279px (десктопы)
 * - xl: 1280px - 1535px (большие экраны)
 * - 2xl: >= 1536px (огромные экраны)
 */

import { useState, useEffect } from 'react';

interface ResponsiveState {
  isMobile: boolean;      // < 768px
  isTablet: boolean;      // 768px - 1023px
  isDesktop: boolean;     // >= 1024px
  isSmallMobile: boolean; // < 480px
  isLarge: boolean;       // >= 1280px
  width: number;
  height: number;
}

/**
 * Хук для работы с media queries
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(query).matches;
  });

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const mediaQuery = window.matchMedia(query);

    const handleChange = (event: MediaQueryListEvent) => {
      setMatches(event.matches);
    };

    // Устанавливаем начальное значение
    setMatches(mediaQuery.matches);

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }

    // Fallback для Safari < 14 и старых браузеров
    if (typeof mediaQuery.addListener === 'function') {
      mediaQuery.addListener(handleChange);
      return () => mediaQuery.removeListener(handleChange);
    }

    return undefined;
  }, [query]);

  return matches;
}

/**
 * Основной хук для определения типа устройства
 * 
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { isMobile, isTablet, isDesktop } = useResponsive();
 *   
 *   return (
 *     <div>
 *       {isMobile && <MobileView />}
 *       {isTablet && <TabletView />}
 *       {isDesktop && <DesktopView />}
 *     </div>
 *   );
 * }
 * ```
 */
export function useResponsive(): ResponsiveState {
  const [dimensions, setDimensions] = useState(() => {
    if (typeof window === 'undefined') {
      return { width: 1920, height: 1080 };
    }
    return {
      width: window.innerWidth,
      height: window.innerHeight,
    };
  });

  // Media queries
  const isMobile = useMediaQuery('(max-width: 767px)');
  const isTablet = useMediaQuery('(min-width: 768px) and (max-width: 1023px)');
  const isDesktop = useMediaQuery('(min-width: 1024px)');
  const isSmallMobile = useMediaQuery('(max-width: 479px)');
  const isLarge = useMediaQuery('(min-width: 1280px)');

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleResize = () => {
      setDimensions({
        width: window.innerWidth,
        height: window.innerHeight,
      });
    };

    // Debounce для оптимизации
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    const debouncedResize = () => {
      if (timeoutId) {
      clearTimeout(timeoutId);
      }
      timeoutId = setTimeout(handleResize, 150);
    };

    window.addEventListener('resize', debouncedResize);

    return () => {
      if (timeoutId) {
      clearTimeout(timeoutId);
      }
      window.removeEventListener('resize', debouncedResize);
    };
  }, []);

  return {
    isMobile,
    isTablet,
    isDesktop,
    isSmallMobile,
    isLarge,
    width: dimensions.width,
    height: dimensions.height,
  };
}

/**
 * Хук для получения текущего breakpoint
 * 
 * @example
 * ```tsx
 * const breakpoint = useBreakpoint();
 * // 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl'
 * ```
 */
export function useBreakpoint(): 'xs' | 'sm' | 'md' | 'lg' | 'xl' | '2xl' {
  const { width } = useResponsive();

  if (width < 640) return 'xs';
  if (width < 768) return 'sm';
  if (width < 1024) return 'md';
  if (width < 1280) return 'lg';
  if (width < 1536) return 'xl';
  return '2xl';
}

